import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import AppIcon from '../components/AppIcon';
import PhoneInput from '../components/PhoneInput';
import { PageSeo } from '../components/Seo';
import { getErrorMessage } from '../api/errors';
import { useAuth } from '../context/AuthContext';
import { useProducts } from '../context/ProductContext';
import { preOrdersService } from '../services/preOrders/preOrdersService';
import type { PreOrder as PreOrderRecord } from '../types';
import { formatPrice } from '../utils/formatPrice';
import {
  canPreOrder,
  getPrimaryImage,
  getProductUrl,
} from '../utils/product';
import { validateInternationalPhone } from '../utils/phone';
import { hasPendingPreOrder } from '../utils/preOrders';

export default function PreOrder() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { getProductBySlug, loadProductBySlug, isLoading } = useProducts();

  const [ready, setReady] = useState(Boolean(slug && getProductBySlug(slug)));
  const [quantity, setQuantity] = useState(1);
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [existingPreOrders, setExistingPreOrders] = useState<PreOrderRecord[]>([]);

  const product = slug ? getProductBySlug(slug) : undefined;
  const hasPendingForProduct = product
    ? hasPendingPreOrder(existingPreOrders, product.id)
    : false;

  useEffect(() => {
    void preOrdersService.fetchPreOrders().then(setExistingPreOrders).catch(() => {});
  }, []);

  useEffect(() => {
    if (!slug) {
      setReady(true);
      return;
    }

    if (getProductBySlug(slug)) {
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);

    void loadProductBySlug(slug).finally(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [slug, getProductBySlug, loadProductBySlug]);

  useEffect(() => {
    setPhone(user?.phone ?? '');
  }, [user?.phone]);

  if (!ready || (isLoading && !product)) {
    return (
      <div className="container empty-state">
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container empty-state">
        <PageSeo title="Ürün Bulunamadı" description="Aradığınız ürün bulunamadı." noindex />
        <h1>Ürün bulunamadı</h1>
        <Link to="/shop" className="btn btn-primary">Ürünlere Dön</Link>
      </div>
    );
  }

  if (product.inStock) {
    return <Navigate to={getProductUrl(product.slug)} replace />;
  }

  if (!canPreOrder(product)) {
    return (
      <div className="container empty-state">
        <PageSeo title="Ön Sipariş" noindex />
        <h1>Bu ürün için ön sipariş alınamıyor</h1>
        <p>Fiyat bilgisi için lütfen bizimle iletişime geçin.</p>
        <Link to={getProductUrl(product.slug)} className="btn btn-primary">
          Ürüne Dön
        </Link>
      </div>
    );
  }

  if (hasPendingForProduct) {
    return (
      <div className="container pre-order-page">
        <PageSeo title="Ön Sipariş" noindex />
        <Link to={getProductUrl(product.slug)} className="back-link">
          <ArrowLeft size={16} aria-hidden />
          Ürüne Dön
        </Link>
        <h1>Ön Sipariş Mevcut</h1>
        <div className="pre-order-existing">
          <p>
            <strong>{product.name}</strong> için zaten bekleyen bir ön siparişiniz var.
            Stok geldiğinde sizi bilgilendireceğiz.
          </p>
          <Link to="/pre-orders" className="btn btn-primary pre-order-existing-link">
            Ön Siparişlerimi Gör
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    const trimmedPhone = phone.trim();
    const phoneError = validateInternationalPhone(trimmedPhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    if (!user) {
      setError('Ön sipariş oluşturmak için giriş yapmalısınız.');
      return;
    }

    setSubmitting(true);
    try {
      await preOrdersService.createPreOrder({
        productId: product.id,
        quantity,
        contact: {
          name: user.name,
          email: user.email,
          phone: trimmedPhone,
        },
        note: note.trim() || undefined,
      });
      setCompleted(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Ön sipariş oluşturulamadı.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return (
      <div className="container pre-order-page">
        <PageSeo title="Ön Sipariş Alındı" noindex />
        <div className="pre-order-success">
          <span className="pre-order-success-icon">
            <AppIcon icon={CheckCircle2} size={48} />
          </span>
          <h1>Ön siparişiniz alındı</h1>
          <p>
            <strong>{product.name}</strong> için {quantity} adet ön sipariş talebiniz kaydedildi.
            Stok geldiğinde sizi bilgilendireceğiz.
          </p>
          <div className="pre-order-success-actions">
            <Link to="/pre-orders" className="btn btn-outline">Ön Siparişlerim</Link>
            <Link to="/shop" className="btn btn-outline">Alışverişe Devam Et</Link>
            <Link to={getProductUrl(product.slug)} className="btn btn-primary">Ürüne Dön</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pre-order-page">
      <PageSeo
        title={`Ön Sipariş — ${product.name}`}
        description={`${product.name} için ön sipariş oluşturun.`}
        noindex
      />

      <Link to={getProductUrl(product.slug)} className="back-link">
        <ArrowLeft size={16} aria-hidden />
        Ürüne Dön
      </Link>

      <h1>Ön Sipariş Oluştur</h1>
      <p className="pre-order-lead">
        Bu ürün şu an stokta yok. Ön sipariş vererek stok geldiğinde bilgilendirilmenizi sağlayabilirsiniz.
      </p>

      <div className="pre-order-layout">
        <aside className="pre-order-summary">
          <img src={getPrimaryImage(product)} alt={product.name} />
          <h2>{product.name}</h2>
          <p className="pre-order-price">{formatPrice(product)}</p>
          <span className="out-of-stock-badge">Stokta Yok</span>
        </aside>

        <form className="checkout-form pre-order-form" onSubmit={handleSubmit}>
          {error && <div className="auth-error">{error}</div>}

          <section>
            <h2>Talep Bilgileri</h2>

            <div className="form-group">
              <label htmlFor="pre-order-name">Ad Soyad</label>
              <input id="pre-order-name" value={user?.name ?? ''} readOnly disabled />
            </div>

            <div className="form-group">
              <label htmlFor="pre-order-email">E-posta</label>
              <input id="pre-order-email" value={user?.email ?? ''} readOnly disabled />
            </div>

            <div className="form-group">
              <label htmlFor="pre-order-phone">Telefon *</label>
              <PhoneInput
                id="pre-order-phone"
                value={phone}
                onChange={setPhone}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="pre-order-quantity">Adet *</label>
              <div className="quantity-controls">
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                  aria-label="Azalt"
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span>{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.min(99, value + 1))}
                  aria-label="Artır"
                  disabled={quantity >= 99}
                >
                  +
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pre-order-note">Not (isteğe bağlı)</label>
              <textarea
                id="pre-order-note"
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Eklemek istediğiniz bir not varsa yazabilirsiniz."
              />
            </div>
          </section>

          <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
            {submitting ? 'Gönderiliyor...' : 'Ön Sipariş Oluştur'}
          </button>
        </form>
      </div>
    </div>
  );
}
